import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailLink,
  signInWithPhoneNumber,
  linkWithPhoneNumber,
  unlink,
  PhoneAuthProvider,
  RecaptchaVerifier,
  initializeRecaptchaConfig,
  signOut,
  deleteUser,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase/config';
import { registerUserProfile, deleteUserProfile } from './registration';
import {
  PUBLIC_REGISTRATION_ENABLED,
  INSTRUCTOR_REGISTRATION_ENABLED,
  STUDENT_REGISTRATION_ENABLED,
  STORAGE_KEYS,
  USER_ROLE,
} from '../utils/constants';
import { normalizeEmail } from '../utils/emailUtils';
import { normalizePhoneTR } from '../utils/phoneUtils';
import { assertIdentitiesAvailable } from './identityUniqueness';

let phoneRecaptcha = null;
/** Kurulumu süren/biten doğrulayıcı; aynı alan için tekrar kurulmaz */
let phoneRecaptchaSetup = null;
let recaptchaConfigPromise = null;
/** SMS isteği bunu beklemez; kod doğrulanmadan önce tamamlanmış olmalı */
let phonePrepare = null;
/** Çift tıklamada aynı SMS isteğinin iki kez gitmesini engeller */
let phoneSendInFlight = null;
let emailOtpSendInFlight = null;

const SMS_REQUEST_TIMEOUT_MS = 60000;
const RECAPTCHA_SETUP_TIMEOUT_MS = 20000;

const isLocalDevHost = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
};

const withTimeout = (promise, ms, errorMessage) =>
  Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(errorMessage)), ms);
    }),
  ]);

const ensureRecaptchaConfig = async () => {
  try {
    if (!recaptchaConfigPromise) {
      recaptchaConfigPromise = Promise.resolve(initializeRecaptchaConfig(auth));
    }
    await recaptchaConfigPromise;
  } catch (err) {
    console.warn('reCAPTCHA config yüklenemedi (v2’ye düşülecek):', err);
    recaptchaConfigPromise = null;
  }
};

const clearPhoneRecaptcha = () => {
  phoneRecaptchaSetup = null;
  if (!phoneRecaptcha) return;
  try {
    phoneRecaptcha.clear();
  } catch {
    /* verifier zaten temizlenmiş olabilir */
  }
  phoneRecaptcha = null;
};

const ensureRecaptchaContainer = (containerId) => {
  if (typeof document === 'undefined') {
    throw new Error('RECAPTCHA_MISSING');
  }
  let el = document.getElementById(containerId);
  if (!el) {
    el = document.createElement('div');
    el.id = containerId;
    document.body.appendChild(el);
  }
  el.innerHTML = '';
  return el;
};

/**
 * Invisible reCAPTCHA — kullanıcı tıklaması beklemez; takılmayı önlemek için timeout ile kullanılır.
 * Firebase her gönderimden sonra doğrulayıcıyı kendisi sıfırlar; "Kodu tekrar gönder" aynı
 * doğrulayıcıyı kullanır, betik ve yapılandırma yeniden yüklenmez.
 */
const getPhoneRecaptcha = (containerId) => {
  if (phoneRecaptchaSetup?.containerId === containerId) return phoneRecaptchaSetup.promise;
  clearPhoneRecaptcha();
  const setup = { containerId };
  setup.promise = (async () => {
    await ensureRecaptchaConfig();
    if (phoneRecaptchaSetup !== setup) throw new Error('RECAPTCHA_MISSING');
    ensureRecaptchaContainer(containerId);
    const verifier = new RecaptchaVerifier(auth, containerId, {
      size: 'invisible',
      callback: () => {
        setup.solvedAt = Date.now();
      },
      'expired-callback': () => {
        if (phoneRecaptchaSetup === setup) clearPhoneRecaptcha();
      },
    });
    phoneRecaptcha = verifier;
    if (typeof verifier.render === 'function') {
      await verifier.render();
    }
    return verifier;
  })();
  setup.promise.catch(() => {
    if (phoneRecaptchaSetup === setup) clearPhoneRecaptcha();
  });
  phoneRecaptchaSetup = setup;
  return setup.promise;
};

/** Telefon numarası yazılırken doğrulayıcıyı arka planda hazırlar; düğmeye basınca beklenmez */
export function warmPhoneRecaptcha(containerId = 'recaptcha-container') {
  if (typeof document === 'undefined' || !document.getElementById(containerId)) return;
  if (isLocalDevHost() && process.env.NODE_ENV !== 'test') return;
  getPhoneRecaptcha(containerId).catch((err) => {
    console.warn('reCAPTCHA ön hazırlığı:', err);
  });
}

const RECAPTCHA_CHALLENGE_SELECTOR = 'iframe[src*="/recaptcha/api2/bframe"], iframe[src*="/recaptcha/enterprise/bframe"]';

const isChallengeOpen = (frame) => {
  const rect = frame.getBoundingClientRect();
  if (rect.height <= 0 || rect.bottom <= 0) return false;
  for (let el = frame; el && el !== document.body; el = el.parentElement) {
    if (window.getComputedStyle(el).visibility === 'hidden') return false;
  }
  return true;
};

/**
 * Resim bulmacası dışına dokunularak kapatılırsa reCAPTCHA ne çözüm ne hata bildirir; Firebase
 * isteği zaman aşımına kadar bekler. Bulmaca açılıp gizlendiyse ve çözüm gelmediyse istek bitirilir.
 * Çözülen bulmaca da gizlenir; çözüm bildirimi kısa süre içinde gelir.
 */
export function watchRecaptchaChallengeDismissal(setup, { pollMs = 200, graceMs = 2000 } = {}) {
  let poll = null;
  const promise = new Promise((_, reject) => {
    if (typeof document === 'undefined') return;
    let wasOpen = false;
    let hiddenSince = 0;
    poll = setInterval(() => {
      const open = [...document.querySelectorAll(RECAPTCHA_CHALLENGE_SELECTOR)].some(isChallengeOpen);
      if (open) {
        wasOpen = true;
        hiddenSince = 0;
        return;
      }
      if (!wasOpen || setup?.solvedAt) return;
      if (!hiddenSince) hiddenSince = Date.now();
      else if (Date.now() - hiddenSince >= graceMs) {
        clearInterval(poll);
        reject(new Error('RECAPTCHA_DISMISSED'));
      }
    }, pollMs);
  });
  return {
    promise,
    stop: () => {
      if (poll) clearInterval(poll);
    },
  };
}

/** SMS isteğini, bulmaca kapatılınca hemen bitecek şekilde gönderir */
const sendWithRecaptcha = async (verifier, send) => {
  if (phoneRecaptchaSetup) phoneRecaptchaSetup.solvedAt = 0;
  const dismissal = watchRecaptchaChallengeDismissal(phoneRecaptchaSetup);
  try {
    return await withTimeout(
      Promise.race([send(verifier), dismissal.promise]),
      SMS_REQUEST_TIMEOUT_MS,
      'SMS_TIMEOUT'
    );
  } finally {
    dismissal.stop();
  }
};

const resetPhoneRecaptchaAfterError = (err, containerId) => {
  clearPhoneRecaptcha();
  if (err?.message === 'RECAPTCHA_DISMISSED') warmPhoneRecaptcha(containerId);
};

const authErrorPayload = (err) => {
  const code = err?.code || '';
  const message = String(err?.message || '');
  const custom = err?.customData?.message || err?.customData?._tokenResponse?.error?.message || '';
  return `${code} ${message} ${custom}`.toLowerCase();
};

/** Telefon / SMS hatalarını kullanıcıya anlaşılır Türkçe mesaja çevirir */
export function describePhoneAuthError(err) {
  if (!err) return 'İşlem başarısız oldu.';
  if (err.message === 'INVALID_PHONE') return 'Geçerli bir cep telefonu numarası girin.';
  if (err.message === 'INVALID_SMS_CODE' || err.code === 'auth/invalid-verification-code') {
    return 'SMS kodu hatalı.';
  }
  if (err.message === 'PHONE_CODE_PENDING') return 'Önce SMS kodu isteyin.';
  if (err.message === 'RECAPTCHA_MISSING') {
    return 'Doğrulama alanı yüklenemedi. Sayfayı yenileyip tekrar deneyin.';
  }
  if (err.message === 'oturum gerekli') {
    return 'Telefon bağlamak için önce oturum açın.';
  }
  if (err.message === 'SMS_TIMEOUT') {
    return 'İstek uzun sürdü, SMS isteği tamamlanamadı. Lütfen tekrar deneyin.';
  }
  if (err.code === 'auth/network-request-failed') {
    return 'Bağlantı hatası. İnternet bağlantınızı kontrol edip tekrar deneyin.';
  }
  if (err.message === 'RECAPTCHA_DISMISSED') {
    return 'Doğrulama penceresi kapatıldı, SMS gönderilmedi. Tekrar deneyip resimleri seçerek doğrulayın.';
  }
  if (err.message === 'LOCALHOST_SMS_BLOCKED') {
    return 'Bu ortamda SMS gönderilemiyor. Canlı site üzerinden deneyin.';
  }
  if (err.message === 'INVALID_EMAIL') return 'Geçerli bir e-posta adresi girin.';
  if (err.message === 'INVALID_NAME') return 'Ad soyad girin.';
  if (err.message === 'INVALID_REGISTRATION') return 'Kayıt bilgileri eksik.';
  if (err.message === 'INVALID_EMAIL_CODE') return 'E-posta kodu hatalı.';
  if (err.message === 'EMAIL_CODE_PENDING') return 'Önce e-posta kodu isteyin.';
  if (err.message === 'EMAIL_CODE_EXPIRED') return 'Kodun süresi doldu. Yeni kod isteyin.';
  if (err.message === 'EMAIL_CHANGED_RELOGIN') {
    return 'E-posta güncellendi. Yeni adresinizle yeniden giriş yapın.';
  }
  if (err.message === 'EMAIL_CHANGE_FAILED') {
    return err.messageDetail || 'E-posta değiştirilemedi. Lütfen tekrar deneyin.';
  }
  if (err.message === 'EMAIL_CODE_RATE_LIMIT') {
    return err.messageDetail || 'Çok fazla deneme. Biraz bekleyip yeni kod isteyin.';
  }
  if (err.message === 'ACCOUNT_EXISTS') {
    return err.messageDetail || 'Bu bilgilerle kayıtlı bir hesap var. Giriş yapın.';
  }
  if (err.message === 'INVALID_REGISTRATION_INPUT') {
    return err.messageDetail || 'Kayıt bilgileri geçersiz.';
  }
  if (err.message === 'IDENTITY_TAKEN' || err.code === 'identity-taken') {
    return 'Bu e-posta veya telefon başka bir hesapta kullanılıyor.';
  }
  if (err.message === 'REGISTRATION_CLOSED') {
    return 'Bu rol için kayıt şu anda kapalı.';
  }
  if (err.code === 'auth/operation-not-allowed') {
    return 'Telefon ile giriş şu an kullanılamıyor. E-posta kodu ile deneyin veya destek ile iletişime geçin.';
  }
  if (err.code === 'auth/billing-not-enabled') {
    return 'SMS gönderimi şu an aktif değil. E-posta ile devam edin veya destek ile iletişime geçin.';
  }
  if (err.code === 'auth/too-many-requests') {
    return 'Çok fazla deneme yapıldı. Biraz bekleyin veya e-posta kodu ile giriş yapın.';
  }
  if (err.code === 'auth/quota-exceeded') {
    return 'Günlük SMS limiti doldu. Daha sonra tekrar deneyin veya e-posta ile giriş yapın.';
  }

  const payload = authErrorPayload(err);
  const isAppCredential =
    err.code === 'auth/invalid-app-credential' ||
    err.code === 'auth/captcha-check-failed' ||
    err.code === 'auth/missing-client-identifier' ||
    payload.includes('invalid_app_credential') ||
    payload.includes('captcha_check_failed');

  if (err.code === 'auth/unauthorized-domain' || payload.includes('hostname match not found')) {
    return 'Bu site adresi SMS girişi için henüz tanımlı değil. Şimdilik e-posta kodu ile giriş yapın ve durumu yöneticiye bildirin.';
  }

  if (isAppCredential) {
    if (isLocalDevHost()) {
      return 'Bu ortamda SMS doğrulama çalışmayabilir. Canlı site üzerinden deneyin.';
    }
    return 'SMS doğrulama tamamlanamadı. Sayfayı yenileyip tekrar deneyin.';
  }

  if (err.code === 'auth/invalid-phone-number') {
    return 'Geçersiz telefon numarası. 05XX XXX XX XX formatında girin.';
  }
  if (err.code === 'auth/code-expired' || err.code === 'auth/session-expired') {
    return 'SMS kodunun süresi doldu. Yeni kod isteyin.';
  }
  if (err.code === 'auth/credential-already-in-use') {
    return 'Bu telefon başka bir hesapta kayıtlı.';
  }
  if (err.code === 'auth/email-already-in-use') {
    return 'Bu e-posta başka bir hesapta kayıtlı.';
  }
  if (err.code === 'auth/requires-recent-login') {
    return 'Güvenlik için e-posta veya SMS ile yeniden giriş yapıp tekrar deneyin.';
  }
  if (err.code === 'functions/resource-exhausted' || err.code === 'resource-exhausted') {
    return err.message || 'Çok fazla deneme. Biraz bekleyin.';
  }
  if (err.code === 'functions/deadline-exceeded' || err.code === 'deadline-exceeded') {
    return 'Kodun süresi doldu. Yeni kod isteyin.';
  }
  if (err.code === 'functions/permission-denied' || err.code === 'permission-denied') {
    if (/kod/i.test(String(err.message || ''))) return 'E-posta kodu hatalı.';
  }
  if (err.code === 'functions/failed-precondition' || err.code === 'failed-precondition') {
    return 'Önce e-posta kodu isteyin.';
  }
  if (err.code === 'functions/invalid-argument' || err.code === 'invalid-argument') {
    return err.message || 'Geçersiz istek.';
  }
  if (err.code === 'functions/unavailable' || err.code === 'unavailable') {
    return 'Doğrulama servisi şu an yanıt vermiyor. Biraz sonra tekrar deneyin.';
  }
  return 'İşlem başarısız oldu. Lütfen tekrar deneyin.';
}

const mapEmailOtpCallableError = (err) => {
  const code = err?.code || '';
  const message = String(err?.message || '');
  if (code === 'functions/invalid-argument' || /geçerli bir e-posta/i.test(message)) {
    return new Error('INVALID_EMAIL');
  }
  if (code === 'functions/failed-precondition') {
    return new Error('EMAIL_CODE_PENDING');
  }
  if (code === 'functions/deadline-exceeded' || /süresi doldu/i.test(message)) {
    return new Error('EMAIL_CODE_EXPIRED');
  }
  if (code === 'functions/resource-exhausted') {
    const mapped = new Error('EMAIL_CODE_RATE_LIMIT');
    mapped.messageDetail = message;
    return mapped;
  }
  if (code === 'functions/permission-denied' || /kodu hatalı/i.test(message)) {
    return new Error('INVALID_EMAIL_CODE');
  }
  return err;
};

/** E-posta ile şifresiz giriş — kutuya 6 haneli kod gider (link yok) */
export async function sendEmailSignInCode(email) {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail || !normalizedEmail.includes('@')) {
    throw new Error('INVALID_EMAIL');
  }
  if (emailOtpSendInFlight) {
    return emailOtpSendInFlight;
  }

  emailOtpSendInFlight = (async () => {
    try {
      const requestCode = httpsCallable(functions, 'requestEmailSignInCode');
      await requestCode({ email: normalizedEmail });
      window.localStorage.setItem(STORAGE_KEYS.EMAIL_SIGN_IN, normalizedEmail);
      window.localStorage.setItem(STORAGE_KEYS.EMAIL_SIGN_IN_AT, String(Date.now()));
      return normalizedEmail;
    } catch (err) {
      throw mapEmailOtpCallableError(err);
    } finally {
      emailOtpSendInFlight = null;
    }
  })();

  return emailOtpSendInFlight;
}

/** Kod 10 dk geçerli; iOS Safari Mail uygulamasına geçişte sekmeyi yeniden yükleyebilir */
const EMAIL_CODE_RESUME_MS = 10 * 60 * 1000;

export function getPendingEmailSignIn(now = Date.now()) {
  const email = normalizeEmail(window.localStorage.getItem(STORAGE_KEYS.EMAIL_SIGN_IN));
  const sentAt = Number(window.localStorage.getItem(STORAGE_KEYS.EMAIL_SIGN_IN_AT));
  if (!email || !Number.isFinite(sentAt) || sentAt <= 0) return null;
  if (now - sentAt >= EMAIL_CODE_RESUME_MS) return null;
  return { email, sentAt };
}

export function clearPendingEmailSignIn() {
  window.localStorage.removeItem(STORAGE_KEYS.EMAIL_SIGN_IN);
  window.localStorage.removeItem(STORAGE_KEYS.EMAIL_SIGN_IN_AT);
}

/** @deprecated Eski magic-link API — OTP kullanın */
export async function sendEmailSignInLink(email) {
  return sendEmailSignInCode(email);
}

export async function confirmEmailSignInCode(email, code) {
  const normalizedEmail = normalizeEmail(
    email || window.localStorage.getItem(STORAGE_KEYS.EMAIL_SIGN_IN)
  );
  if (!normalizedEmail) {
    throw new Error('EMAIL_CODE_PENDING');
  }
  const trimmed = String(code || '').trim();
  if (!/^\d{6}$/.test(trimmed)) {
    throw new Error('INVALID_EMAIL_CODE');
  }
  try {
    const verifyCode = httpsCallable(functions, 'verifyEmailSignInCode');
    const result = await verifyCode({ email: normalizedEmail, code: trimmed });
    const signInKey = result?.data?.signInKey;
    if (!signInKey) {
      throw new Error('EMAIL_CODE_PENDING');
    }
    const { user } = await signInWithEmailLink(auth, normalizedEmail, signInKey);
    clearPendingEmailSignIn();
    return user;
  } catch (err) {
    if (err?.message === 'EMAIL_CODE_PENDING' || err?.message === 'INVALID_EMAIL_CODE') {
      throw err;
    }
    throw mapEmailOtpCallableError(err);
  }
}

const EMAIL_CHANGE_SERVER_CODES = new Set([
  'functions/invalid-argument', 'functions/already-exists', 'functions/resource-exhausted',
  'functions/failed-precondition', 'functions/deadline-exceeded', 'functions/permission-denied',
  'functions/not-found',
]);

const mapEmailChangeError = (err) => {
  const mapped = new Error('EMAIL_CHANGE_FAILED');
  const bareCode = String(err?.code || '').replace('functions/', '');
  // İstemci zaman aşımı gibi SDK hataları İngilizce kod metniyle gelir; yalnızca sunucu mesajı gösterilir
  if (EMAIL_CHANGE_SERVER_CODES.has(err?.code) && err?.message && err.message !== bareCode) {
    mapped.messageDetail = err.message;
  }
  return mapped;
};

const normalizeNewEmail = (newEmail) => {
  const normalizedEmail = normalizeEmail(newEmail);
  if (!normalizedEmail || !normalizedEmail.includes('@')) {
    throw new Error('INVALID_EMAIL');
  }
  return normalizedEmail;
};

/** Yeni adrese 6 haneli doğrulama kodu gönderir */
export async function requestEmailChangeCode(newEmail) {
  if (!auth.currentUser) {
    throw new Error('oturum gerekli');
  }
  const normalizedEmail = normalizeNewEmail(newEmail);
  try {
    await httpsCallable(functions, 'requestEmailChangeCode')({ newEmail: normalizedEmail });
  } catch (err) {
    throw mapEmailChangeError(err);
  }
  return normalizedEmail;
}

/** Kod doğruysa sunucu Auth + profil + bağlı kayıtları günceller; oturum yeni adresle sürer */
export async function confirmEmailChangeCode(code, expectedEmail = '') {
  if (!auth.currentUser) {
    throw new Error('oturum gerekli');
  }
  const trimmed = String(code || '').trim();
  if (!/^\d{6}$/.test(trimmed)) {
    throw new Error('INVALID_EMAIL_CODE');
  }
  const expected = normalizeEmail(expectedEmail);
  let result;
  try {
    result = await httpsCallable(functions, 'confirmEmailChange')({ code: trimmed });
  } catch (err) {
    // Yanıt yolda kaybolsa bile değişiklik sunucuda tamamlanmış olabilir; oturum o durumda iptal edilmiştir
    const changed = await auth.currentUser.reload()
      .then(() => Boolean(expected) && normalizeEmail(auth.currentUser?.email) === expected)
      .catch((reloadErr) => reloadErr?.code === 'auth/user-token-expired');
    throw changed ? new Error('EMAIL_CHANGED_RELOGIN') : mapEmailChangeError(err);
  }
  const email = normalizeEmail(result?.data?.email) || expected;
  const signInKey = result?.data?.signInKey;
  if (!signInKey) {
    throw new Error('EMAIL_CHANGED_RELOGIN');
  }
  await signInWithEmailLink(auth, email, signInKey);
  return email;
}

/** Eğitmen: kendi öğrencisinin e-postasını kodsuz değiştirir */
export async function updateStudentEmailByInstructor(studentId, newEmail) {
  const normalizedEmail = normalizeNewEmail(newEmail);
  try {
    const result = await httpsCallable(functions, 'instructorUpdateStudentEmail')({
      studentId,
      newEmail: normalizedEmail,
    });
    return normalizeEmail(result?.data?.email) || normalizedEmail;
  } catch (err) {
    throw mapEmailChangeError(err);
  }
}

/** Eğitmen hesabını sunucuda anında aktif oluşturur (onay beklenmez) */
async function registerInstructorOnServer({ name, contact }) {
  try {
    await httpsCallable(functions, 'registerInstructor')({
      name,
      contact,
      termsAccepted: true,
    });
  } catch (err) {
    const code = err?.code || '';
    if (code === 'functions/already-exists') {
      const mapped = new Error('ACCOUNT_EXISTS');
      mapped.messageDetail = err.message;
      throw mapped;
    }
    if (code === 'functions/invalid-argument') {
      const mapped = new Error('INVALID_REGISTRATION_INPUT');
      mapped.messageDetail = err.message;
      throw mapped;
    }
    throw err;
  }
}

/** Eğitmen kaydı (e-posta) — hesap anında aktif olur, ardından giriş kodu gönderilir */
export async function startInstructorEmailRegistration({ email, name }) {
  if (!INSTRUCTOR_REGISTRATION_ENABLED) {
    throw new Error('REGISTRATION_CLOSED');
  }
  const trimmedName = String(name || '').trim();
  if (!trimmedName) {
    throw new Error('INVALID_NAME');
  }
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail || !normalizedEmail.includes('@')) {
    throw new Error('INVALID_EMAIL');
  }
  await registerInstructorOnServer({ name: trimmedName, contact: normalizedEmail });
  await sendEmailSignInCode(normalizedEmail);
  return normalizedEmail;
}

/** Eğitmen kaydı (telefon) — hesap anında aktif olur, ardından SMS kodu gönderilir */
export async function startInstructorPhoneRegistration({ phone, name, containerId = 'recaptcha-container' }) {
  if (!INSTRUCTOR_REGISTRATION_ENABLED) {
    throw new Error('REGISTRATION_CLOSED');
  }
  const trimmedName = String(name || '').trim();
  if (!trimmedName) {
    throw new Error('INVALID_NAME');
  }
  const e164 = normalizePhoneTR(phone);
  if (!e164) {
    throw new Error('INVALID_PHONE');
  }
  await registerInstructorOnServer({ name: trimmedName, contact: e164 });
  return sendPhoneSignInCode(e164, containerId);
}

/** Açık kayıt kapalıyken kullanılmaz; şifre yalnızca bu yolda gerekir */
export async function registerWithEmail({ email, password, name, authRole }) {
  if (authRole === USER_ROLE.STUDENT && !STUDENT_REGISTRATION_ENABLED) {
    throw new Error('REGISTRATION_CLOSED');
  }
  if (authRole === USER_ROLE.INSTRUCTOR && !INSTRUCTOR_REGISTRATION_ENABLED) {
    throw new Error('REGISTRATION_CLOSED');
  }
  if (!PUBLIC_REGISTRATION_ENABLED) {
    throw new Error('REGISTRATION_CLOSED');
  }
  const normalizedEmail = normalizeEmail(email);
  const userCredential = await createUserWithEmailAndPassword(
    auth,
    normalizedEmail,
    password.trim()
  );
  const user = userCredential.user;
  try {
    await sendEmailVerification(user);
    await registerUserProfile({ user, authRole, name });
    await signOut(auth);
    return user;
  } catch (err) {
    try {
      await deleteUserProfile(user.uid, authRole);
      await deleteUser(user);
    } catch {
      await signOut(auth);
    }
    throw err;
  }
}

/** Eğitmenin eklediği öğrencinin telefon girişi aynı UID'ye düşsün diye Auth hesabını hazırlar */
async function prepareStudentPhoneLogin(e164) {
  try {
    await withTimeout(
      httpsCallable(functions, 'prepareStudentPhoneLogin')({ phone: e164 }),
      15000,
      'PREPARE_TIMEOUT'
    );
  } catch (err) {
    console.warn('Telefon girişi hazırlığı atlandı:', err);
  }
}

export async function sendPhoneSignInCode(phone, containerId = 'recaptcha-container') {
  const e164 = normalizePhoneTR(phone);
  if (!e164) {
    throw new Error('INVALID_PHONE');
  }
  if (isLocalDevHost() && process.env.NODE_ENV !== 'test') {
    throw new Error('LOCALHOST_SMS_BLOCKED');
  }
  if (phoneSendInFlight) {
    return phoneSendInFlight;
  }

  phoneSendInFlight = (async () => {
    try {
      auth.languageCode = 'tr';
      phonePrepare = prepareStudentPhoneLogin(e164);
      const verifier = await withTimeout(
        getPhoneRecaptcha(containerId), RECAPTCHA_SETUP_TIMEOUT_MS, 'SMS_TIMEOUT'
      );
      return await sendWithRecaptcha(verifier, (v) => signInWithPhoneNumber(auth, e164, v));
    } catch (err) {
      resetPhoneRecaptchaAfterError(err, containerId);
      throw err;
    } finally {
      phoneSendInFlight = null;
    }
  })();

  return phoneSendInFlight;
}

export async function confirmPhoneSignInCode(confirmation, code) {
  if (!confirmation) {
    throw new Error('PHONE_CODE_PENDING');
  }
  const trimmed = String(code || '').trim();
  if (!/^\d{6}$/.test(trimmed)) {
    throw new Error('INVALID_SMS_CODE');
  }
  if (phonePrepare) await phonePrepare;
  const { user } = await confirmation.confirm(trimmed);
  return user;
}

export function resetPhoneRecaptcha() {
  clearPhoneRecaptcha();
}

export async function sendPhoneLinkCode(phone, containerId = 'recaptcha-link-container') {
  if (!auth.currentUser) {
    throw new Error('oturum gerekli');
  }
  const e164 = normalizePhoneTR(phone);
  if (!e164) {
    throw new Error('INVALID_PHONE');
  }
  await assertIdentitiesAvailable([e164], auth.currentUser.uid);
  if (isLocalDevHost() && process.env.NODE_ENV !== 'test') {
    throw new Error('LOCALHOST_SMS_BLOCKED');
  }
  if (phoneSendInFlight) {
    return phoneSendInFlight;
  }

  phoneSendInFlight = (async () => {
    try {
      const existing = auth.currentUser.phoneNumber || '';
      if (existing && existing !== e164) {
        await unlink(auth.currentUser, PhoneAuthProvider.PROVIDER_ID);
      }
      auth.languageCode = 'tr';
      const verifier = await withTimeout(
        getPhoneRecaptcha(containerId), RECAPTCHA_SETUP_TIMEOUT_MS, 'SMS_TIMEOUT'
      );
      return await sendWithRecaptcha(verifier, (v) => linkWithPhoneNumber(auth.currentUser, e164, v));
    } catch (err) {
      resetPhoneRecaptchaAfterError(err, containerId);
      throw err;
    } finally {
      phoneSendInFlight = null;
    }
  })();

  return phoneSendInFlight;
}

export async function confirmPhoneLinkCode(confirmation, code) {
  if (!confirmation) {
    throw new Error('PHONE_CODE_PENDING');
  }
  const trimmed = String(code || '').trim();
  if (!/^\d{6}$/.test(trimmed)) {
    throw new Error('INVALID_SMS_CODE');
  }
  const { user } = await confirmation.confirm(trimmed);
  return user.phoneNumber || '';
}
